import { useMemo, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";

import { Disclose } from "@lopie/kernel-ui";

/**
 * What connects and ingests what — a picture of four columns and the paths
 * between them (LO-13x, from Jason's own follow-up on the Brain map: "we
 * should layer agents and apps in it as well, so we see what connects and
 * ingests what"):
 *
 *     connector  ──writes──>  type  ──may read──>  app  ──declares──>  agent
 *
 * ## This is `apps/agent-os/src/sections/Wiring.jsx`'s own picture, broadened
 *
 * `Wiring` already answers this question and answers it carefully — the
 * declared/observed distinction below is its reasoning, not invented here —
 * but it is scoped to the one question Agent OS exists to answer: *which
 * agents can reach which connectors*, so it draws only the apps that declare
 * an agent and reads only `service_reads`, the unattended half of the
 * ceiling. The Brain screen is asking the broader question — everything
 * that touches this business's data, agent or not — so every app with any
 * declared read appears here, not only the staffed ones, and an app that
 * only a signed-in person may drive is not left off for having no agent.
 *
 * ## Two of those edges are declared and one is derived, and it says so
 *
 * **`type ──may read──> app`** is a fact: an app's own reviewed manifest,
 * `reads` and `service_reads` together — whether or not a person has to be
 * signed in to use it.
 *
 * **`app ──declares──> agent`** is a fact: the agent is named in that same
 * manifest.
 *
 * **`connector ──writes──> type`** is a fact about what has been written,
 * out of the kernel's own count of its graph — not a declaration, and drawn
 * dashed for exactly the reason `Wiring` draws it dashed: nothing configures
 * a pipeline from a connector to whatever reads its type, they only meet
 * there.
 *
 * ## What it does not prove
 *
 * That any of this has actually happened for a real record — a path here is
 * a possibility (the type is declared readable, and the type has been
 * written), and whether those are the same records depends on tags, which
 * this picture does not carry.
 */

const NODE_W = 190;
const NODE_H = 38;
const GAP_Y = 16;
const GAP_X = 92;
const PAD = 12;

const COLUMNS = ["connector", "type", "app", "agent"];

const HEADING = {
  connector: "Brings data in",
  type: "What kind",
  app: "Which app may read it",
  agent: "Which agent runs there",
};

function buildGraph(apps, writers, knownTypes) {
  const connectors = (writers || []).filter((w) => w.kind === "connector");
  const known = new Set(knownTypes || []);

  const readsOf = (app) => {
    const names = new Set();
    for (const g of app.reads || []) names.add(g.type);
    for (const g of app.service_reads || []) names.add(g.type);
    return [...names];
  };

  const written = new Set();
  for (const c of connectors) {
    for (const type of Object.keys(c.objects || {})) written.add(type);
  }
  const read = new Set();
  for (const app of apps) for (const t of readsOf(app)) read.add(t);

  // A type appears if it is on some path and it is one `/schema` actually
  // named — a writer or a manifest can carry a stale type name, and drawing
  // a node for one this org's schema no longer has would be inventing a
  // fourth kind of node out of a typo.
  const types = [...new Set([...written, ...read])]
    .filter((t) => known.has(t))
    .sort();

  const nodes = [];
  const edges = [];
  const push = (kind, id, label, meta) => {
    nodes.push({ kind, id: `${kind}:${id}`, label, meta });
    return `${kind}:${id}`;
  };

  for (const c of connectors) {
    const id = push("connector", c.source, c.source, {
      total: c.total || 0,
      types: Object.keys(c.objects || {}).filter((t) => types.includes(t)),
    });
    for (const [type, n] of Object.entries(c.objects || {})) {
      if (!types.includes(type)) continue;
      edges.push({ from: id, to: `type:${type}`, kind: "writes", n });
    }
  }

  for (const type of types) push("type", type, type, {});

  for (const app of apps) {
    const reads = readsOf(app).filter((t) => types.includes(t));
    if (!reads.length && !(app.agents || []).length) continue;
    const id = push("app", app.app_type, app.app_type, {
      reads, actions: app.actions || [],
    });
    for (const type of reads) {
      edges.push({ from: `type:${type}`, to: id, kind: "reads" });
    }
    for (const agent of app.agents || []) {
      const a = push("agent", `${app.app_type}:${agent.name}`, agent.name, {
        does: agent.does, division: agent.division,
      });
      edges.push({ from: id, to: a, kind: "declares" });
    }
  }

  const byColumn = COLUMNS.map((kind) => nodes.filter((n) => n.kind === kind));
  const tallest = Math.max(...byColumn.map((c) => c.length), 1);
  const height = tallest * NODE_H + (tallest - 1) * GAP_Y + PAD * 2;
  const width = COLUMNS.length * NODE_W + (COLUMNS.length - 1) * GAP_X;

  const at = {};
  byColumn.forEach((column, c) => {
    const span = column.length * NODE_H + (column.length - 1) * GAP_Y;
    const top = (height - span) / 2;
    column.forEach((node, i) => {
      at[node.id] = { x: c * (NODE_W + GAP_X), y: top + i * (NODE_H + GAP_Y) };
    });
  });

  // What lights up when one node is picked: its upstream and its
  // downstream, walked separately, never sideways — see Wiring.jsx's own
  // docstring for why a connected-component walk answers a useless
  // question here (everything ends up related to everything, through a
  // shared type).
  const touching = (id) => {
    if (!id) return null;
    const keep = new Set([id]);
    const walk = (start, next) => {
      const queue = [start];
      while (queue.length) {
        const here = queue.pop();
        for (const e of edges) {
          const [a, b] = next(e);
          if (a !== here || keep.has(b)) continue;
          keep.add(b);
          queue.push(b);
        }
      }
    };
    walk(id, (e) => [e.from, e.to]);
    walk(id, (e) => [e.to, e.from]);
    return keep;
  };

  return { nodes, edges, at, width, height, byColumn, touching, types };
}

export default function DataFlow({ apps, writers, knownTypes, onSelectType }) {
  const [hot, setHot] = useState(null);
  const theme = useTheme();
  // `theme.vars`, not `theme.palette` — see kernel-ui TypeMap.jsx and RecordGraph.jsx
  // for the same fix and why: a raw SVG `fill` set from JS, not from `sx`,
  // has to name the CSS variable explicitly or it renders one colour
  // scheme's value in both.
  const paint = theme.vars?.palette || theme.palette;
  const ink = {
    paper: paint.background.paper,
    text: paint.text.primary,
    quiet: paint.text.secondary,
    line: paint.divider,
    brand: paint.primary.main,
    agent: paint.kernel?.unattended || paint.primary.main,
  };

  const graph = useMemo(
    () => buildGraph(apps || [], writers || [], knownTypes || []),
    [apps, writers, knownTypes],
  );

  if (!graph.nodes.length) return null;

  const lit = graph.touching(hot);
  const on = (id) => !lit || lit.has(id);

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: "space-between", flexWrap: "wrap", mb: 1.5 }}
      >
        <Typography variant="body2" color="text.secondary">
          Hover anything to follow its path. Two of these hops are declared;
          one is observed.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", alignItems: "center" }}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
            <Box sx={{ width: 22, height: 0, borderTop: "2px solid", borderColor: "primary.main" }} />
            <Typography variant="caption" color="text.secondary">declared</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
            <Box sx={{ width: 22, height: 0, borderTop: "2px dashed", borderColor: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">observed — what has been written</Typography>
          </Stack>
        </Stack>
      </Stack>

      <Box sx={{ overflowX: "auto", pb: 1 }}>
        <Box
          component="svg"
          viewBox={`0 0 ${graph.width} ${graph.height}`}
          width={graph.width}
          height={graph.height}
          role="img"
          aria-label={
            `A diagram of ${graph.byColumn[0].length} connectors, ` +
            `${graph.types.length} record types, ${graph.byColumn[2].length} ` +
            `apps and ${graph.byColumn[3].length} agents, and the paths ` +
            "between them. The same information is written out below the diagram."
          }
          sx={{ display: "block", maxWidth: "none", minWidth: graph.width }}
        >
          {graph.edges.map((edge, i) => {
            const a = graph.at[edge.from];
            const b = graph.at[edge.to];
            if (!a || !b) return null;
            const x1 = a.x + NODE_W;
            const y1 = a.y + NODE_H / 2;
            const x2 = b.x;
            const y2 = b.y + NODE_H / 2;
            const mid = (x1 + x2) / 2;
            const on2 = on(edge.from) && on(edge.to);
            return (
              <path
                key={i}
                d={`M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`}
                fill="none"
                stroke={edge.kind === "writes" ? ink.quiet : ink.brand}
                strokeWidth={on2 && lit ? 2 : 1.25}
                strokeDasharray={edge.kind === "writes" ? "4 3" : undefined}
                opacity={on2 ? (lit ? 0.9 : 0.45) : 0.08}
                style={{ transition: "opacity 140ms, stroke-width 140ms" }}
              />
            );
          })}

          {graph.nodes.map((node) => {
            const p = graph.at[node.id];
            const alive = on(node.id);
            const clickable = node.kind === "type" && Boolean(onSelectType);
            return (
              <g
                key={node.id}
                onMouseEnter={() => setHot(node.id)}
                onMouseLeave={() => setHot(null)}
                onFocus={() => setHot(node.id)}
                onBlur={() => setHot(null)}
                onClick={clickable ? () => onSelectType(node.label) : undefined}
                tabIndex={0}
                role="button"
                aria-label={
                  clickable
                    ? `${node.kind} ${node.label}. Show its records.`
                    : `${node.kind} ${node.label}`
                }
                opacity={alive ? 1 : 0.25}
                style={{ cursor: clickable ? "pointer" : "default", transition: "opacity 140ms" }}
              >
                <rect
                  x={p.x}
                  y={p.y}
                  width={NODE_W}
                  height={NODE_H}
                  rx={7}
                  fill={ink.paper}
                  stroke={
                    node.kind === "agent" ? ink.agent
                      : node.kind === "connector" ? ink.quiet
                        : ink.line
                  }
                  strokeWidth={hot === node.id ? 2 : 1}
                />
                <text
                  x={p.x + 12}
                  y={p.y + NODE_H / 2 + 4}
                  fill={ink.text}
                  fontSize={12.5}
                  fontFamily={node.kind === "type" ? "inherit" : "ui-monospace, monospace"}
                  fontWeight={node.kind === "agent" ? 600 : 400}
                >
                  {node.label}
                </text>
              </g>
            );
          })}
        </Box>
      </Box>

      <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", rowGap: 1, mt: 1 }}>
        {COLUMNS.map((kind, i) => (
          <Box key={kind} sx={{ minWidth: 0, flex: "1 1 150px" }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
              {HEADING[kind]}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {graph.byColumn[i].length}
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* The same paths, in words. An SVG is not keyboard-navigable on its
          own and colour is not a signal every reader gets — the same
          argument RecordGraph and TypeMap make about their own diagrams. */}
      <Disclose
        align="row"
        label="The same thing in sentences"
        count={graph.byColumn[2].length + graph.byColumn[3].length}
      >
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {graph.byColumn[2].map((app) => {
            const reach = graph.touching(app.id);
            const from = [...reach]
              .filter((id) => id.startsWith("connector:"))
              .map((id) => id.slice("connector:".length));
            const agents = [...reach]
              .filter((id) => id.startsWith("agent:"))
              .map((id) => graph.nodes.find((n) => n.id === id)?.label)
              .filter(Boolean);
            return (
              <Typography key={app.id} variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600 }}>{app.label}</Box>{" "}
                may read {app.meta.reads.join(", ") || "nothing"}.{" "}
                {from.length
                  ? `Records of that have been written by ${from.join(", ")} — nothing connects them; they meet at the type.`
                  : "No connector has written a record of any type it may read."}{" "}
                {agents.length
                  ? `Runs unattended as ${agents.join(", ")}.`
                  : "Declares no agent — only a signed-in person drives it."}
              </Typography>
            );
          })}
          {graph.byColumn[0]
            .filter((c) => !graph.touching(c.id).size || [...graph.touching(c.id)].every((id) => !id.startsWith("app:")))
            .map((c) => (
              <Typography key={c.id} variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600 }}>{c.label}</Box>{" "}
                writes {c.meta.types.join(", ") || "nothing"}, and no app
                declares a read for{" "}
                {c.meta.types.length === 1 ? "that type" : "any of those types"}.
                That is a fact about what is declared, not a fault.
              </Typography>
            ))}
        </Stack>
      </Disclose>
    </Box>
  );
}
